"use client";

import React from "react";
import MenuColumn from "./MenuColumn";
import { MenuNode } from "./menuAdapter";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";

interface MegaMenuPanelProps {
  title: string;
  categories: MenuNode[];
  activePath: string[];
  hoveredPath: string[];
  lookupMap: Map<string, MenuNode>;
  align?: "left" | "right";
  onItemHover: (level: number, node: MenuNode) => void;
  onClose: () => void;
}

interface ColumnData {
  title: string;
  items: MenuNode[];
  activeSlug?: string;
  hoveredSlug?: string;
}

export default function MegaMenuPanel({
  title,
  categories,
  activePath,
  hoveredPath,
  lookupMap,
  align = "left",
  onItemHover,
}: MegaMenuPanelProps) {
  const { zIndex } = HEADER_V2_DESIGN_SYSTEM;

  // Resolve columns dynamically for arbitrary category depth
  const columns: ColumnData[] = [];

  // 1. Column 0 (Root Level) is always rendered (displays the parent category name as header)
  columns.push({
    title: title,
    items: categories,
    activeSlug: activePath[0],
    hoveredSlug: hoveredPath[0],
  });

  // 2. Dynamically resolve children columns by traversing the active path
  for (let level = 0; level < activePath.length; level++) {
    const activeSlug = activePath[level];
    const activeNode = lookupMap.get(activeSlug);
    
    if (activeNode && activeNode.children && activeNode.children.length > 0) {
      columns.push({
        title: activeNode.label,
        items: activeNode.children,
        activeSlug: activePath[level + 1],
        hoveredSlug: hoveredPath[level + 1],
      });
    } else {
      break;
    }
  }

  const getColumnWidthClass = (idx: number) => {
    switch (idx) {
      case 0:
        return "w-[250px]"; // increased from 202px
      case 1:
        return "w-[350px]"; // increased from 284px
      case 2:
        return "w-[380px]"; // increased from 324px
      default:
        return "w-[350px]";
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes megaMenuEnter {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.985);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-megamenu-enter {
          animation: megaMenuEnter 130ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .custom-thin-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-thin-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-thin-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.4);
          border-radius: 4px;
        }
        .custom-thin-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.6);
        }
      `}} />
      <div
        onClick={(e) => e.stopPropagation()}
        className={`absolute top-full mt-1 ${
          align === "right" ? "right-0" : "left-0"
        } bg-white rounded-[12px] border border-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.08)] ${zIndex.dropdown} animate-megamenu-enter flex flex-row divide-x divide-slate-100 h-fit max-h-none w-fit min-w-0`}
      >
        {columns.map((col, idx) => (
          <MenuColumn
            key={`${col.title}-${idx}`}
            title={col.title}
            items={col.items}
            activeSlug={col.activeSlug}
            hoveredSlug={col.hoveredSlug}
            widthClass={getColumnWidthClass(idx)}
            onItemHover={(node) => onItemHover(idx, node)}
            onItemClick={() => {}}
          />
        ))}
      </div>
    </>
  );
}
